import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { AddressLabel } from "@/components/address-label";
import { Input } from "@/components/ui/input";
import { CustomSelect } from "@/components/ui/custom-select";
import { statesAndLgas, leadwayStatesAndLgas } from "@/lib/states-and-lgas";
import { DeliveryAddress } from "../types";
import { AlertCircle } from "lucide-react";
import { useHMOStore } from "@/features/hmo-consultation/hooks/use-hmo-store";
import { leadwayIds } from "@/lib/utils";

interface DeliveryAddressModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (address: DeliveryAddress) => void;
}

export function DrugDeliveryAddressModal({
  isOpen,
  onClose,
  onSave,
}: DeliveryAddressModalProps) {

   const appState = useHMOStore();
    const providerId = appState?.partnerInfo?.providerId;
  
    const isLeadwayProvider = !!providerId && leadwayIds.includes(providerId);
  const [error, setError] = useState<string | null>(null);
  const [address, setAddress] = useState<DeliveryAddress>({
    street: "",
    city: "",
    landmark: "",
    state: "",
    lga: "",
  });



  // Clear errors and reset form when modal closes
  const handleClose = () => {
    setError(null);
    setAddress({
      street: "",
      city: "",
      landmark: "",
      state: "",
      lga: "",
    });
    onClose();
  };

  const handleUpdateAddress = (name: string, value: string) => {
    setError(null);

    if (!name || name === "") return;
    if (name === "state") {
      setAddress((prev) => ({
        ...prev,
        lga: "",
        [name]: value,
      }));
    } else {
      setAddress((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleSave = () => {
    // Check if required fields are filled
    const missingFields = [];
    if (!address.state) missingFields.push("State");
    if (!address.lga) missingFields.push("LGA");
    if (!address.city) missingFields.push("City");
    if (!address.street) missingFields.push("Street");

    if (missingFields.length > 0) {
      setError(`Please fill in the following required fields: ${missingFields.join(", ")}`);
      return;
    }

    onSave(address);
    handleClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md max-h-[90vh] flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle>Enter your delivery address</DialogTitle>
          <DialogDescription>
            This address will be used to deliver your drugs.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4 px-1 overflow-y-auto flex-1 min-h-0">

          <div>
            <AddressLabel htmlFor="street" className="mb-1 block" required>Street</AddressLabel>
            <Input
              id="street"
              value={address.street}
              onChange={(e) => handleUpdateAddress("street", e.target.value)}
              placeholder="Enter your street address"
            />
          </div>

          <div>
            <AddressLabel htmlFor="city" className="mb-1 block" required>City</AddressLabel>
            <Input
              id="city"
              value={address.city}
              onChange={(e) => handleUpdateAddress("city", e.target.value)}
              placeholder="Enter your city"
            />
          </div>

          <div>
            <AddressLabel htmlFor="state" className="mb-1 block" required>State</AddressLabel>
            <CustomSelect
              id="state"
              value={address.state}
              onChange={(e) => handleUpdateAddress("state", e.target.value)}
              options={Object.keys(isLeadwayProvider ? leadwayStatesAndLgas : statesAndLgas).map((state) => ({
                value: state,
                label: state,
              }))}
              placeholder="Select a state"
            />
          </div>

          <div>
            <AddressLabel htmlFor="lga" className="mb-1 block" required>LGA</AddressLabel>
            <CustomSelect
              id="lga"
              value={address.lga}
              onChange={(e) => handleUpdateAddress("lga", e.target.value)}
              disabled={!address.state}
              options={(isLeadwayProvider ? leadwayStatesAndLgas[address.state] || [] : statesAndLgas[address.state] || []).map((lga) => ({
                value: lga,
                label: lga,
              }))}
              placeholder={address.state ? "Select LGA" : "Select a state first"}
            />
          </div>

          <div>
            <AddressLabel htmlFor="landmark" className="mb-1 block">Closest Landmark</AddressLabel>
            <Input
              id="landmark"
              value={address.landmark || ""}
              onChange={(e) => handleUpdateAddress("landmark", e.target.value)}
              placeholder="Enter the closest landmark (optional)"
            />
          </div>

        </div>

        {/* Fixed footer with buttons */}
        <div className="flex-shrink-0 pt-4 border-t border-gray-200">
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
              <div className="flex items-start gap-2">
                <AlertCircle className="h-4 w-4 text-red-600 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-red-700 font-medium">
                  {error}
                </p>
              </div>
            </div>
          )}

          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            <Button onClick={handleSave}>
              Save Address
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
